import type { components } from "@/core/api/schema";
import { formatClock } from "@/core/format/format";

export type MealSlot = components["schemas"]["MealSlot"];
export type MealSlotBody = components["schemas"]["CreateMealSlotRequest"];
export type MealSlotChanges = components["schemas"]["UpdateMealSlotRequest"];

/** The API's limits (brands.constant.ts, MEAL_SLOT_LIMITS). */
export const MEAL_SLOT_LIMITS = { maxActive: 6, nameMaxLength: 30 } as const;

/** What the slot editor holds, as typed. */
export interface MealSlotForm {
  name: string;
  /** "0" = the meal's own day, "1" = the day before. */
  cutoffDaysBefore: "0" | "1";
  cutoffTime: string;
  /** "" = not set. */
  deliveryTime: string;
  active: boolean;
}
export type MealSlotErrors = Partial<Record<"name" | "cutoffTime" | "deliveryTime", string>>;

const TIME = /^([01]\d|2[0-3]):[0-5]\d$/;

export const inSlotOrder = <T extends { position: number }>(slots: readonly T[]) =>
  [...slots].sort((a, b) => a.position - b.position);

export const newSlotForm = (): MealSlotForm => ({
  name: "",
  cutoffDaysBefore: "0",
  cutoffTime: "",
  deliveryTime: "",
  active: true,
});

export const formFromSlot = (slot: MealSlot): MealSlotForm => ({
  name: slot.name,
  cutoffDaysBefore: slot.cutoffDaysBefore === 1 ? "1" : "0",
  cutoffTime: slot.cutoffTime,
  deliveryTime: slot.deliveryTime ?? "",
  active: slot.active,
});

/** Checks the way the API will (a name another slot of the brand uses is checked by the API). */
export function validateSlotForm(form: MealSlotForm): MealSlotErrors {
  const errors: MealSlotErrors = {};
  const name = form.name.trim();
  if (!name) errors.name = "Give it a name, like Lunch or Evening snacks.";
  else if (name.length > MEAL_SLOT_LIMITS.nameMaxLength)
    errors.name = `At most ${MEAL_SLOT_LIMITS.nameMaxLength} characters.`;
  if (!TIME.test(form.cutoffTime)) errors.cutoffTime = "A time like 10:30.";
  if (form.deliveryTime && !TIME.test(form.deliveryTime))
    errors.deliveryTime = "A time like 12:30, or leave it empty.";
  else if (
    !errors.cutoffTime &&
    form.cutoffDaysBefore === "0" &&
    form.deliveryTime &&
    form.cutoffTime >= form.deliveryTime
  )
    errors.cutoffTime = "On the same day, the cutoff must be before the usual delivery time.";
  return errors;
}

export const toSlotBody = (form: MealSlotForm): MealSlotBody => ({
  name: form.name.trim(),
  cutoffDaysBefore: Number(form.cutoffDaysBefore),
  cutoffTime: form.cutoffTime,
  deliveryTime: form.deliveryTime || null,
});

/** Only what changed (the API refuses an empty change). */
export function toSlotChanges(form: MealSlotForm, slot: MealSlot): MealSlotChanges {
  const body = toSlotBody(form);
  const changes: MealSlotChanges = {};
  if (body.name !== slot.name) changes.name = body.name;
  if (body.cutoffDaysBefore !== slot.cutoffDaysBefore) changes.cutoffDaysBefore = body.cutoffDaysBefore;
  if (body.cutoffTime !== slot.cutoffTime) changes.cutoffTime = body.cutoffTime;
  if (body.deliveryTime !== slot.deliveryTime) changes.deliveryTime = body.deliveryTime ?? null;
  if (form.active !== slot.active) changes.active = form.active;
  return changes;
}

/** "10:30 am the same day" / "9:00 pm the day before". */
export const describeCutoff = (slot: Pick<MealSlot, "cutoffDaysBefore" | "cutoffTime">) =>
  `${formatClock(slot.cutoffTime)} ${slot.cutoffDaysBefore === 1 ? "the day before" : "the same day"}`;
